/**
 * LLM-assisted shaping: write an idea as multiple candidate types
 *
 * Given one idea (title + description), generates alternative framings for each
 * candidate type (problem, opportunity, risk, solution, commitment, investment)
 * and identifies which framing best matches the original idea.
 *
 * Registry: shaping — inline assist. Transport: callFeatureLlm.
 */

import type { CandidateLanguageType } from '../types/prioritization/lifecycle';
import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const CANDIDATE_TYPES: CandidateLanguageType[] = [
  'problem',
  'opportunity',
  'risk',
  'solution',
  'commitment',
  'investment',
];

const SHAPING_SYSTEM_PROMPT =
  'You rewrite prioritization ideas into six frame types (problem, opportunity, risk, solution, commitment, investment). Respond with valid JSON only.';

export interface GeneratedFrame {
  frameType: CandidateLanguageType;
  title: string;
  description: string;
}

export interface GenerateFramesResult {
  frames: GeneratedFrame[];
  originalType: CandidateLanguageType;
  reasoning?: string;
}

export async function generateFramesFromIdea(idea: {
  title: string;
  description?: string;
}): Promise<GenerateFramesResult> {
  if (!isAiCapabilityEnabled('shaping') || !idea.title.trim()) {
    return { frames: [], originalType: 'opportunity' };
  }

  try {
    const content = await callFeatureLlm({
      userPrompt: buildShapingPrompt(idea),
      systemPrompt: SHAPING_SYSTEM_PROMPT,
      conversationId: 'shaping-generate-frames-' + Date.now(),
      model: DEFAULT_MODEL,
      temperature: 0.4,
      maxTokens: 1000,
    });
    if (!content) return { frames: [], originalType: 'opportunity' };
    return parseShapingResponse(content);
  } catch (error) {
    console.warn('LLM shaping (generate frames) failed:', error);
    return { frames: [], originalType: 'opportunity' };
  }
}

function buildShapingPrompt(idea: { title: string; description?: string }): string {
  const desc = idea.description?.trim() ? `\nDescription: ${idea.description}` : '';
  return `You are helping shape prioritization items. One idea can be expressed in different "frames": as a problem, opportunity, risk, solution, commitment, or investment. Your task is to take a single idea and rewrite it in each of these six frames.

Idea:
Title: ${idea.title}${desc}

For each frame type, provide a short title and 1-2 sentence description that express the SAME underlying idea in that frame:
- problem: what is wrong that needs fixing
- opportunity: what we could gain
- risk: what might go wrong if we don't act
- solution: a specific approach we could take
- commitment: an external promise we must keep
- investment: a strategic bet for future value

Also indicate which single frame type best matches the original idea as written (originalType).

Return JSON only, no markdown or extra text:
{
  "frames": [
    { "frameType": "problem", "title": "...", "description": "..." },
    { "frameType": "opportunity", "title": "...", "description": "..." },
    { "frameType": "risk", "title": "...", "description": "..." },
    { "frameType": "solution", "title": "...", "description": "..." },
    { "frameType": "commitment", "title": "...", "description": "..." },
    { "frameType": "investment", "title": "...", "description": "..." }
  ],
  "originalType": "opportunity",
  "reasoning": "Optional one sentence"
}

Include all six frame types. Use the exact frameType strings above.`;
}

function parseShapingResponse(content: string): GenerateFramesResult {
  const defaultResult: GenerateFramesResult = {
    frames: [],
    originalType: 'opportunity',
  };

  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return defaultResult;

    const parsed = JSON.parse(jsonMatch[0]);
    const rawFrames = Array.isArray(parsed.frames) ? parsed.frames : [];
    const frames: GeneratedFrame[] = rawFrames
      .filter(
        (f: unknown) =>
          f &&
          typeof f === 'object' &&
          CANDIDATE_TYPES.includes((f as { frameType?: string }).frameType as CandidateLanguageType) &&
          typeof (f as { title?: unknown }).title === 'string' &&
          typeof (f as { description?: unknown }).description === 'string',
      )
      .map((f: Record<string, unknown>) => ({
        frameType: f.frameType as CandidateLanguageType,
        title: String(f.title).trim(),
        description: String(f.description ?? '').trim(),
      }));

    const originalType = CANDIDATE_TYPES.includes(parsed.originalType as CandidateLanguageType)
      ? (parsed.originalType as CandidateLanguageType)
      : 'opportunity';

    return {
      frames,
      originalType,
      reasoning: typeof parsed.reasoning === 'string' ? parsed.reasoning : undefined,
    };
  } catch (e) {
    console.warn('Parse shaping response failed:', e);
    return defaultResult;
  }
}
